import { Form, Menu, Select, message, Spin } from '@nackle/paper';
import { Check, FileEmpty } from '@nackle/icons';
import { RosterPerson, searchPeople } from 'app/api/roster';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';
import React, { Component, ReactNode } from 'react';
import { buildNotFoundPersonFromId } from 'shared/person-search-field/buildMockPerson';
import { buildPersonSearchRequests } from 'shared/person-search-field/buildQueries';
import { debounceUsingPromises } from 'shared/person-search-field/debounceUsingPromises';
import { formatPerson } from 'shared/person-search-field/format';
import { GUID } from 'shared/person-search-field/regexes';
import { defined } from 'utils/defined';

export interface RosterPersonMap { [personId: string]: RosterPerson | undefined }

interface Props {
    helperText?: any['helperText'];
    intent?: any['intent'];
    id: any['id'];
    label: any['label'];
    myPersonId?: string;
    onChange: (personIds: string[], persons: RosterPersonMap) => void;
    personIds: string[];
    placeholder?: any['placeholder'];
}

interface State {
    searchResults: RosterPerson[];
    resultObjectCache: RosterPersonMap;
}


/** Type specialization for MultiSelect so Typescript enforces that `T` is `RosterPerson` **/
// class RosterPersonMultiSelect extends null<RosterPerson> {}

export class PersonSearchField extends Component<Props, State> {
    state: State = {
        searchResults: [],
        resultObjectCache: {},
    };

    public componentDidMount(): void {
        this.queryPeople();
        this.props.personIds.map(personId => this.ensurePersonLoadedById(personId));
    }

    public componentDidUpdate(): void {
        this.props.personIds.map(personId => this.ensurePersonLoadedById(personId));
    }

    public returnPersonIds(): string[] {
        return this.state.searchResults.map(person => person.id);
    }

    public render() {
        
        const {
            helperText,
            label,
            placeholder = 'Search for people by first name, last name',
        } = this.props;

        return (
                <Form.Item
                help={helperText}
                label={label}
                >
                <Select
                    showSearch
                    autoClearSearchValue
                    allowClear
                    placeholder={placeholder}
                    onSearch={this.handleQueryChange}
                    onChange={this.handleChange}
                    onClear={this.handleClear}
                    filterOption = {false}
                    notFoundContent={this.state.searchResults.length != 0 ? <Spin size="small" /> : null}
                >
                    {this.state.searchResults.map(person =>
                        <option key={person.id } value={person.id}>{person.givenName + ' ' + person.surname}</option>
                    )};
                </Select>
                </Form.Item>
        );
    }

    private personItemRenderer = (person: RosterPerson, itemProps: any) => (
        <Menu
            data-menu-item-id={person.id}
            data-menu-item-given-name={person.givenName}
            data-menu-item-surname={person.surname}
            data-menu-item-is-me={this.props.myPersonId && person.id === this.props.myPersonId}
            key={person.id}
            title={this.props.myPersonId && person.id === this.props.myPersonId ? 'Me' : undefined}
            onClick={itemProps.handleClick}
            mode="inline"
            >
                <Menu.Item icon={this.props.personIds.includes(person.id) ? <Check/> : <FileEmpty/>} style={{ height: 20, margin: 0, borderRadius: 3 }}> {formatPerson(person)} </Menu.Item>
            </Menu>
    );

    private popoverProps: Partial<any> = {
        minimal: true,
    };

    /**
     * Attach additional props to the tag elements so Selenium tests can tell what's going on
     */
    private personTagGetProps = (value: ReactNode): any => {
        // Because MultiSelect "renders" the tag content before passing it to TagInput,
        // the only way to pass information to this function is to either figure out which person
        // maps to the `index` or extract it from the React element's props.

        if (!value || typeof value !== 'object' || !('props' in value)) {
            return {};
        }

        const personId = value.props['data-pass-person-id-to-prop-getter'];
        if (!personId) {
            return {};
        }

        const person = this.state.resultObjectCache[personId] || null;

        return {
            'data-person-id': personId,
            'data-person-given-name': person?.givenName,
            'data-person-surname': person?.surname,
            'data-person-is-me': this.props.myPersonId && personId === this.props.myPersonId,
        } as any;
    };

    /**
     * Render the content of each "person" tag added to the UI.
     */
    private personTagRenderer = (person: RosterPerson) => (
        <span data-pass-person-id-to-prop-getter={person.id}>
            {`${person.givenName} ${person.surname}`}
        </span>
    );

    private handleTogglePerson = (person: RosterPerson) => {
        const newPersonIds = this.props.personIds.slice();

        const personIndex = newPersonIds.indexOf(person.id);
        if (personIndex < 0) {
            newPersonIds.push(person.id);
        } else {
            newPersonIds.splice(personIndex, 1);
        }

        this.handleChange(newPersonIds[0]);
    };

    private handleRemovePerson = (value: string, index: number) => {
        const newPersonIds = [...this.props.personIds];
        newPersonIds.splice(index, 1);
        this.handleChange(newPersonIds[0]);
    };

    private handleChange = (personIds: string) => {
        const persons = {[personIds]: this.state.resultObjectCache[personIds]};
        const personIdArry: string[] = [personIds];
        this.props.onChange(personIdArry, persons);
    };

    private handleQueryChange = (query: string) => {
        this.queryPeople(query);
    };

    private handleClear = () => {
        this.setState({ searchResults: [] });
    };

    private ensurePersonLoadedById = async (personId?: string) => {
        try {
            if (!personId) return;
            if (this.state.resultObjectCache[personId]) return;

            const results = await searchPeople({ personId });
            const person = results.persons[0] || buildNotFoundPersonFromId(personId);
            await this.addSeenPeople([person]);
        } catch (e) {
            console.error(`Error looking up for ${JSON.stringify(personId)}`, e);
            message.error({
                content: 'An error occurred.',
            });
        }
    };

    private queryPeople = debounceUsingPromises(async (searchText = '') => {
        try {
            if (searchText.length <= 0){
                this.setState({ searchResults: [] });
                return;
            }
            const searchRequests = buildPersonSearchRequests(searchText);
            const results = await Promise.all(searchRequests.map(searchPeople));
            const allPersons = results.flatMap((result) => result.persons);
            const uniquePersons: RosterPerson[] = uniqBy(allPersons, (person: RosterPerson) => person.id);
            await this.addSeenPeople(uniquePersons);

            // Return the "cached" person objects if one exists instead of the ones from the server
            // so that the uncontrolled component can maintain state across queries
            const filteredResults = sortBy<RosterPerson>(
                uniquePersons.map((person) => defined(this.state.resultObjectCache[person.id])),
                ['givenName', 'surname'],
            );

            // Allow "searching" for arbitrary GUIDs because the Arcade is also used with non-Roster systems
            // (i.e. Daymaker <= 6.4) and we want to still be able to look up tokens associated with those user IDs
            if (searchText.match(GUID) && !filteredResults.some(({ id }) => id === searchText)) {
                filteredResults.push(buildNotFoundPersonFromId(searchText));
            }

            this.setState({ searchResults: filteredResults });
            
        } catch (e) {
            console.error(`Error querying for ${JSON.stringify(searchText)}`, e);
            message.warning({
                content: 'An error occurred.',
            });
        }
    });

    private addSeenPeople(people: RosterPerson[]): Promise<void> {
        return new Promise((resolve) => {
            this.setState((previous: State) => {
                const seenResults = { ...previous.resultObjectCache };
                for (const result of people) {
                    if (!seenResults[result.id]) {
                        seenResults[result.id] = result;
                    }
                }
                return { resultObjectCache: seenResults };
            }, resolve);
        });
    }
}
